/*
Contents of this file was produced with a help of a code generation tool and subsequently reviewed and edited by a human.

While some of the code was created by an AI assisted service, manual adjustments have been made to ensure correctness, readability, functionality and compliance to Avalara's coding standards and guidelines.
Any future modifications should be approached with caution to avoid overwriting the manual changes.

This source code is owned by Avalara Inc. Unauthorized copying of this file, via any medium, is strictly prohibited. Proprietary and confidential.

For more information, refer to the documentation or contact the development team.
*/

// remove the line below once we npm install in the v3 folder in CI/CD
/* eslint-disable import/no-unresolved */

import { Component, Element, Event, EventEmitter, Host, Method, Prop, h, Listen, Watch, State, forceUpdate } from '@stencil/core';
import { IntlShape } from '@formatjs/intl';
import { state } from '../../store';
import parseJson from '../../utils/parse-json';

@Component({
  tag: 's-dialog',
  styleUrl: 's-dialog.scss',
  shadow: false,
})
export class SDialog {
  @Listen('s-intlupdate', { target: 'window' })
  intlUpdateHandler() {
    forceUpdate(this);
  }

  @Prop({ attribute: 'nodismiss' }) noDismiss?: boolean;

  @Prop({ attribute: 'backgroundclose' }) backgroundClose?: boolean;

  @Prop({ mutable: true, reflect: true }) open?: boolean = false;

  @Prop({ mutable: true }) role = 'dialog';

  @Prop({ attribute: 'i18n' }) i18n?: Record<string, string> | string = {};

  @Element() element: HTMLSDialogElement;

  @State() elementToFocus: HTMLElement;

  @State() i18nJson: Record<string, string> = {};

  #previousFocusedElement: Element;

  #returnValue = '';

  #timerHandle;

  intl: IntlShape;

  @Event({
    eventName: 's-dismiss',
    bubbles: true,
  })
  sDismiss: EventEmitter<Record<string, unknown>>;

  @Event({
    eventName: 'close',
    bubbles: true,
  })
  sClose: EventEmitter<Record<string, unknown>>;

  @Watch('open')
  watchOpen(newValue: boolean) {
    this.open = newValue;

    if (newValue) {
      // ref: https://github.com/ionic-team/stencil/issues/3246#issuecomment-1048802446
      clearTimeout(this.#timerHandle);
      this.#previousFocusedElement = document.activeElement || null;

      this.#timerHandle = setTimeout(() => this.#focusFirstFocusableChild(), 0);
    }

    document.body.style.overflow = 'hidden';

    if (!newValue) {
      this.sDismiss.emit({ id: this.element.id, returnValue: this.#returnValue });
      this.sClose.emit({ id: this.element.id, returnValue: this.#returnValue });
      document.body.style.overflow = 'unset';

      if (this.#previousFocusedElement) {
        this.elementToFocus = this.#previousFocusedElement as HTMLElement;
      }
    }
  }

  @Watch('i18n')
  watchI18n() {
    this.i18nJson = parseJson(this.i18n, "Please provide valid JSON string on dialogs's i18n attribute", {});
  }

  @Method()
  async showModal() {
    this.open = true;
  }

  @Method()
  async close(returnValue) {
    this.#returnValue = returnValue;
    this.open = false;
    clearTimeout(this.#timerHandle);
  }

  @Listen('keydown', { target: 'document' })
  handleKeyDown(e: KeyboardEvent) {
    if (!this.open) return;

    switch (e.key) {
      case 'Escape':
        e.stopPropagation();
        this.open = false;
        break;
      case 'Tab':
        if (document.activeElement === this.#allFocusableElementsInDialog[this.#allFocusableElementsInDialog.length - 1] && !e.shiftKey) {
          e.preventDefault();
          (this.#allFocusableElementsInDialog[0] as HTMLElement).focus();
          break;
        }
        if (document.activeElement === this.#allFocusableElementsInDialog[0] && e.shiftKey) {
          e.preventDefault();
          (this.#allFocusableElementsInDialog[this.#allFocusableElementsInDialog.length - 1] as HTMLElement).focus();
          break;
        }
        break;
      default:
        break;
    }
  }

  @Listen('click')
  handleBackgroundClick(e: MouseEvent) {
    if (!this.backgroundClose || !this.open) return;

    const boundingRectTopLeft =
      this.element.querySelector('[slot="header"]')?.getBoundingClientRect() || this.element.querySelector('[data-ref="content-container"]').getBoundingClientRect();
    const boundingRectBottomRight =
      this.element.querySelector('[slot="footer"]')?.getBoundingClientRect() || this.element.querySelector('[data-ref="content-container"]').getBoundingClientRect();
    if (e.clientX > boundingRectTopLeft.left && e.clientX < boundingRectBottomRight.right && e.clientY > boundingRectTopLeft.top && e.clientY < boundingRectBottomRight.bottom) {
      return;
    }

    this.open = false;
  }

  static get focusableElements() {
    return 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  }

  #focusFirstElement(elements) {
    if (elements.length) {
      [this.elementToFocus] = elements;
      return true;
    }
    return false;
  }

  get #dialogText() {
    const { intl } = this;
    return {
      ariaLabelClose:
        this.i18nJson['dialog.ariaLabel.close'] ||
        intl.formatMessage({
          id: 'skylab.dialog.ariaLabel.close',
          defaultMessage: 'Close',
        }),
    };
  }

  get #allFocusableElementsInDialog() {
    return this.element.querySelectorAll(`${SDialog.focusableElements}`);
  }

  #focusFirstFocusableChild() {
    let isFocused = false;

    const contentFocusableElements = this.element.querySelector("[data-ref='content-container']").querySelectorAll(SDialog.focusableElements);
    isFocused = this.#focusFirstElement(contentFocusableElements);

    // check all areas of dialog for focusable elements
    const header = this.element.querySelector('[slot="header"]');
    const footer = this.element.querySelector('[slot="footer"]');

    if (footer && !isFocused) {
      const footerFocusableElements = footer.querySelectorAll(SDialog.focusableElements);
      isFocused = this.#focusFirstElement(footerFocusableElements);
    }
    if (header && !isFocused) {
      const headerFocusableElements = header.querySelectorAll(SDialog.focusableElements);
      isFocused = this.#focusFirstElement(headerFocusableElements);
    }

    if (!isFocused) {
      const allFocusableElements = this.element.querySelectorAll(SDialog.focusableElements);
      isFocused = this.#focusFirstElement(allFocusableElements);
    }
  }

  componentDidRender() {
    this.elementToFocus?.focus();
  }

  componentWillUpdate() {
    state.intl
      .then(value => {
        this.intl = value as IntlShape;
      })
      .catch(err => {
        console.error('Error creating intl object:', err);
      });
  }

  connectedCallback() {
    // a11y (see doc site about this)
    if (!this.role || this.role !== 'alertdialog') this.role = 'dialog';

    const content = Array.from(this.element.children);

    if (content.some(item => !item.hasAttribute('slot'))) {
      console.error('s-dialog: All children elements must have a slot attribute set.');
    }
    state.intl
      .then(value => {
        this.intl = value as IntlShape;
        this.watchI18n();
      })
      .catch(err => {
        console.error('Error creating intl object:', err);
      });
  }

  disconnectedCallback() {
    clearTimeout(this.#timerHandle);
  }

  render() {
    if (!this.intl) return null;
    return (
      <Host role={this.role} aria-modal="true">
        <slot name="header"></slot>
        {!this.noDismiss ? (
          <button
            type="button"
            data-ref="dismiss-button"
            onClick={() => {
              this.open = false;
            }}
            class="ghost icon"
            aria-label={this.#dialogText.ariaLabelClose}
            slot="button"
          >
            <s-icon name="close" aria-hidden="true"></s-icon>
          </button>
        ) : (
          ''
        )}
        <div data-ref="content-container" slot="">
          <slot name="body"></slot>
        </div>
        <slot name="footer"></slot>
      </Host>
    );
  }
}
